import { Box, IconButton, SxProps, TextField, Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useDebounce } from "../../hooks/debounce";

// "medium" is the historical field (~40px with the theme's dense margins);
// "small" drops those margins and tightens the input so it sits in a card
// header at about 28px, the height of a compact text input.
type QuickSearchSize = "small" | "medium";

const FilterBackground = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth" && prop !== "size",
})<{ fullWidth?: boolean; size: QuickSearchSize }>(
  ({ theme, fullWidth, size }) => ({
    flex: fullWidth ? "1 1 auto" : "0 1 224px",
    maxWidth: fullWidth ? "none" : undefined,
    width: fullWidth ? "100%" : undefined,
    backgroundColor: theme.vars.palette.background.input,
    border: `1px solid ${theme.vars.palette.border.inputOutlined}`,
    borderRadius: "6px",
    paddingLeft: theme.spacing(size === "small" ? 1 : 1.375),
    paddingRight: theme.spacing(size === "small" ? 1 : 1.375),
    ...(size === "small" && {
      "& .MuiTextField-root": { marginTop: 0, marginBottom: 0 },
    }),
    "& input": {
      fontSize: size === "small" ? "0.8125rem" : "0.875rem",
      ...(size === "small" && { paddingTop: 2, paddingBottom: 3 }),
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
      display: "none",
    },
    "& svg": {
      fill: theme.vars.palette.text.secondary,
    },
  })
);

interface Props {
  value?: string;
  onChange: (newValue: string) => void;
  fullWidth?: boolean;
  size?: QuickSearchSize;
  sx?: SxProps<Theme>;
}

export const QuickSearchFilter = ({
  value,
  onChange,
  fullWidth,
  size = "medium",
  sx,
}: Props) => {
  const intl = useIntl();

  const [inputValue, setInputValue] = useState(value || "");

  const debouncedValue = useDebounce(inputValue, 250);

  const onChangeRef = useRef(onChange);

  useEffect(() => onChangeRef.current(debouncedValue), [debouncedValue]);

  useEffect(() => {
    setInputValue((prev) => (prev !== value ? value || "" : prev));
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const iconSize = size === "small" ? "small" : "medium";

  return (
    <FilterBackground fullWidth={fullWidth} size={size} sx={sx}>
      <TextField
        fullWidth
        variant="standard"
        value={inputValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setInputValue(event.target.value)
        }
        placeholder={intl.formatMessage({ id: "SEARCH..." })}
        slotProps={{
          input: {
            endAdornment: inputValue ? (
              <IconButton
                title="Clear"
                aria-label="Clear"
                size="small"
                sx={size === "small" ? { p: 0.25 } : undefined}
                onClick={() => setInputValue("")}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <SearchIcon fontSize={iconSize} />
            ),
          },
        }}
      />
    </FilterBackground>
  );
};
